import React, { useEffect, useState } from "react";
import { useParams } from "react-router-dom";
import { useSelector } from "react-redux";
import styles from "./SaleOrderDetail.module.css";
import WebService from "../Services/WebService";
import CapsuleTabs from "./CapsuleTabs";
import {
  FaCheckCircle,
  FaTimesCircle,
  FaDotCircle,
  FaRegCircle,
} from "react-icons/fa";
import type { RootState } from "../Services/Store";

// --- Status-to-stage configuration ---
const STAGES = [
  {
    key: "CHECKOUT",
    label: "Order Confirmed",
    sublabel: "Your order has been confirmed and is being processed.",
    color: "#10B981",
    icon: <FaCheckCircle color="#10B981" />,
  },
  {
    key: "PLACED",
    label: "Manufacturing Started",
    sublabel: "Manufacturing has started for your order.",
    color: "#0EA5E9",
    icon: <FaDotCircle color="#0EA5E9" />,
  },
  {
    key: "IN_PROCESSING",
    label: "Manufacturing In Progress",
    sublabel: "Your order is being processed.",
    color: "#3B82F6",
    icon: <FaDotCircle color="#3B82F6" />,
  },
  {
    key: "SHIPPED",
    label: "Shipped",
    sublabel: "Your order is Shipped.",
    color: "#F59E42",
    icon: <FaDotCircle color="#F59E42" />,
  },
  {
    key: "COMPLETED",
    label: "Order Delivered",
    sublabel: "Your order has been delivered.",
    color: "#22C55E",
    icon: <FaCheckCircle color="#22C55E" />,
  },
  {
    key: "CANCELED",
    label: "Order Cancelled",
    sublabel: "Your order was cancelled.",
    color: "#EF4444",
    icon: <FaTimesCircle color="#EF4444" />,
  },
  {
    key: "REFUND",
    label: "Order Refunded",
    sublabel: "Your payment was refunded.",
    color: "#64748B",
    icon: <FaDotCircle color="#64748B" />,
  },
  {
    key: "REJECTED",
    label: "Order Rejected",
    sublabel: "Your order was rejected.",
    color: "#EF4444",
    icon: <FaTimesCircle color="#EF4444" />,
  },
];

const isTerminalStatus = (status: string) =>
  ["CANCELED", "REJECTED", "REFUND"].includes(status);

const getStageIndex = (status: string) =>
  STAGES.findIndex((s) => s.key === status);

const currency = (amt?: number | null) =>
  typeof amt === "number" && !isNaN(amt)
    ? "₹" + amt.toLocaleString("en-IN", { minimumFractionDigits: 2 })
    : "₹0.00";

const formatDateTime = (dateStr?: string) =>
  dateStr
    ? new Date(dateStr).toLocaleString("en-IN", {
        year: "numeric",
        month: "short",
        day: "numeric",
        hour: "2-digit",
        minute: "2-digit",
      })
    : "--";

const TrackOrders: React.FC = () => {
  const { orderId } = useParams<{ orderId: string }>();
  const user = useSelector((state: RootState) => state.auth.user);
  const userRole = user?.role_name?.toLowerCase();
  const [orderData, setOrderData] = useState<any>(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (!orderId) return;
    setLoading(true);

    // Use correct endpoint for Admin/Employee vs Customer
    const isCustomer = userRole === "customer";
    const endpoint = isCustomer
      ? `/v1/door-management/saleorders/my-saleorders/${orderId}`
      : `/v1/door-management/saleorders/${orderId}`;

    WebService.getAPI<any>({ action: endpoint })
      .then((res) => {
        if (res?.status_code === 200 && res.data) {
          setOrderData(res.data);
        } else {
          setOrderData(null);
        }
      })
      .catch(() => setOrderData(null))
      .finally(() => setLoading(false));
  }, [orderId, userRole]);

  // Defensive: fallback for missing/unknown statuses
  const currentStatus = orderData?.status || "";
  const currentStageIdx = getStageIndex(currentStatus);

  // --- Timeline events: unique by status, up to the "final" (terminal) status if any
  let saleOrderHistory: any[] = orderData?.sale_order_history || [];
  // Only show up to the final/terminal event, nothing after
  if (isTerminalStatus(currentStatus)) {
    const lastIdx = saleOrderHistory.findIndex(
      (item) => item.status === currentStatus
    );
    if (lastIdx !== -1) saleOrderHistory = saleOrderHistory.slice(0, lastIdx + 1);
  }
  const timelineEvents = saleOrderHistory
    .filter((item, idx, arr) => idx === arr.findIndex(ev => ev.status === item.status)) // Unique by status
    .map((item) => {
      const stage = STAGES.find((s) => s.key === item.status);
      return {
        ...item,
        label: stage?.label || item.status,
        sublabel: stage?.sublabel || "",
        color: stage?.color || "#A1A1AA",
        icon: stage?.icon || <FaRegCircle color="#A1A1AA" />,
        datetime: formatDateTime(item.created_at),
      };
    });

  // --- UI Loading/Error States ---
  if (!orderId)
    return (
      <div className={styles.detailWrapper}>
        <div className={styles.orderBox}>
          <div style={{ color: "#EF4444", fontWeight: 700, padding: 32 }}>
            Invalid URL: No Order ID Provided.
          </div>
        </div>
      </div>
    );
  if (loading)
    return (
      <div className={styles.detailWrapper}>
        <div className={styles.orderBox}>
          <div style={{ color: "#FFD400", fontWeight: 700, padding: 32 }}>
            Loading Order Details...
          </div>
        </div>
      </div>
    );
  if (!orderData)
    return (
      <div className={styles.detailWrapper}>
        <div className={styles.orderBox}>
          <div style={{ color: "#EF4444", fontWeight: 700, padding: 32 }}>
            Order not found or unavailable.
          </div>
        </div>
      </div>
    );

  // --- Progress Bar: halts at terminal status, disables the rest ---
const renderProgressBar = () => {
  type StepState = {
    stage: typeof STAGES[number];
    isCompleted: boolean;
    isCurrent: boolean;
    isDisabled: boolean;
  };

  const stepStates: StepState[] = STAGES.map((stage, idx) => {
    let isCompleted = idx < currentStageIdx;
    let isCurrent = idx === currentStageIdx;
    let isDisabled = idx > currentStageIdx;
    if (isTerminalStatus(currentStatus) && idx > currentStageIdx) {
      isCompleted = false;
      isCurrent = false;
      isDisabled = true;
    }
    return { stage, isCompleted, isCurrent, isDisabled };
  });

  return (
    <div className={styles.progressStepperWrapper}>
      <div className={styles.progressStepperRow}>
        {stepStates.map((step, idx) => (
          <div className={styles.progressStepperDotCol} key={step.stage.key}>
            <div
              className={[
                styles.progressStepperDot,
                step.isCurrent ? styles.active : "",
                step.isCompleted ? styles.completed : "",
              ].join(" ")}
              style={{
                background: step.isCompleted || step.isCurrent ? step.stage.color : "#E5E7EB",
                border: step.isCurrent && isTerminalStatus(currentStatus)
                  ? `2.5px solid #EF4444`
                  : undefined,
              }}
            >
              {step.isCompleted || step.isCurrent
                ? step.stage.icon
                : <FaRegCircle color="#A1A1AA" size={18} />}
            </div>
            {/* Connector: skip last dot */}
            {idx < stepStates.length - 1 && (
              <div
                className={styles.progressStepperConnector}
                style={{
                  background: step.isCompleted || step.isCurrent
                    ? step.stage.color
                    : "#E5E7EB"
                }}
              />
            )}
            <div className={styles.progressStepperLabel}>
              {step.stage.label}
            </div>
          </div>
        ))}
      </div>
    </div>
  );
};



  return (
    <div className={styles.detailWrapper}>
      <CapsuleTabs />

      {/* --- Order Details Card --- */}
      <div className={styles.orderBox} style={{ marginBottom: 28, background: "#fff", borderRadius: 8, boxShadow: "0px 1px 2px rgba(0,0,0,0.05)" }}>
        <div style={{ display: "flex", justifyContent: "space-between", marginBottom: 24, flexWrap: "wrap" }}>
          <div>
            <div style={{ fontWeight: 700, fontSize: 24, color: "#111827", marginBottom: 14 }}>
              Order Details
            </div>
            <div style={{ color: "#4B5563", fontSize: 14, marginBottom: 2 }}>
              Order No:
              <span style={{ color: "#111827", fontWeight: 600, marginLeft: 5 }}>
                #ORD-{orderData?.sale_order_id || "--"}
              </span>
            </div>
            <div style={{ color: "#4B5563", fontSize: 14, marginBottom: 2 }}>
              Customer ID:
              <span style={{ color: "#111827", fontWeight: 600, marginLeft: 5 }}>
                CUST-{orderData?.customer_id || "--"}
              </span>
            </div>
            <div style={{ color: "#4B5563", fontSize: 14 }}>
              Customer name:
              <span style={{ color: "#111827", fontWeight: 600, marginLeft: 5 }}>
                {orderData?.username || "--"}
              </span>
            </div>
          </div>
          <div style={{ minWidth: 200, textAlign: "right" }}>
            <div style={{ color: "#4B5563", fontSize: 14 }}>
              Order Date:
              <span style={{ color: "#111827", fontWeight: 600, marginLeft: 5 }}>
                {orderData?.created_at ? formatDateTime(orderData.created_at) : "--"}
              </span>
            </div>
            <div style={{ color: "#111827", fontWeight: 700, fontSize: 18, margin: "12px 0 0" }}>
              Total Amount: {currency(orderData?.final_amount)}
            </div>
          </div>
        </div>
        {/* --- Progress Bar --- */}
<div>
  <div style={{ fontWeight: 600, fontSize: 18, color: "#111827", marginBottom: 16 }}>Order Progress</div>
  <div className={styles.progressStepperScroll}>
    <div className={styles.progressStepperInner}>
      {renderProgressBar()}
    </div>
  </div>
</div>

      </div>

      {/* --- Track Your Order --- */}
      <div className={styles.orderBox} style={{ marginBottom: 28, background: "#fff", borderRadius: 8, boxShadow: "0px 1px 2px rgba(0,0,0,0.05)" }}>
        <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start", borderBottom: "1px solid #E5E7EB", marginBottom: 18, paddingBottom: 8 }}>
          <div style={{ fontWeight: 700, fontSize: 24, color: "#111827" }}>Track Your Order</div>
          <div style={{ color: "#4B5563", fontSize: 16, fontWeight: 500 }}>
            Due Amount
            <div style={{ color: "#111827", fontWeight: 700, fontSize: 24, marginTop: 4 }}>
              {currency(orderData?.due_amount)}
            </div>
          </div>
        </div>
        <div style={{ fontSize: 15, color: "#4B5563", marginBottom: 10 }}>
          Order ID: #ORD-{orderData?.sale_order_id || "--"}
        </div>
        {/* Timeline */}
        <div style={{ display: "flex", alignItems: "flex-start", position: "relative", marginTop: 28, overflow: "hidden" }}>
        <div style={{
          position: "absolute",
          left: 20,          
          top: 28,          
          bottom: 20,        
          width: 2,
          background: "#10B981",
          zIndex: 0,
        }} />
          <div style={{ width: "100%" }}>
            {timelineEvents.length === 0 ? (
              <div style={{ color: "#aaa", fontWeight: 500, padding: "28px 12px" }}>
                No order history available.
              </div>
            ) : (
              timelineEvents.map((event, idx) => (
                <div key={event.id || idx} style={{ display: "flex", alignItems: "flex-start", marginBottom: 24, position: "relative" }}>
                  
                  <div style={{
                    width: 40, height: 40, borderRadius: "50%",
                    background: event.color,
                    display: "flex", alignItems: "center", justifyContent: "center",
                    marginRight: 18, marginLeft: 0, zIndex: 1, border: `2px solid ${event.color}`,
                  }}>
                    {event.icon}
                  </div>
                 
                  <div style={{
                    minWidth: 250, background: "#fff", borderRadius: 8,
                    padding: 8, boxSizing: "border-box", position: "relative"
                  }}>
                    <div style={{ fontWeight: 700, fontSize: 18, color: "#1E293B", marginBottom: 2 }}>
                      {event.label}
                    </div>
                    <div style={{ fontSize: 13, color: "#64748B", marginBottom: 4 }}>
                      {event.datetime} {idx === timelineEvents.length - 1 ? "- Current Stage" : ""}
                    </div>
                    <div style={{ fontSize: 15, color: "#334155" }}>{event.sublabel}</div>
                    <div style={{ fontSize: 13, color: "#6B7280", marginTop: 3 }}>
                      {event.created_by_username ? `By: ${event.created_by_username}` : ""}
                    </div>
                    
                    {isTerminalStatus(currentStatus) && idx === timelineEvents.length - 1 && (
                      <div style={{ color: "#EF4444", fontWeight: 600, marginTop: 6, fontSize: 13 }}>
                        Need help? Contact support.
                      </div>
                    )}
                  </div>
                </div>
              ))
            )}
          </div>
        </div>
      </div>
      {/* Location Details */}
      <div className={styles.orderBox} style={{ minHeight: 120, background: "#fff", borderRadius: 8, boxShadow: "0px 1px 2px rgba(0,0,0,0.05)" }}>
        <div style={{ fontWeight: 700, fontSize: 20, color: "#111827", marginBottom: 16 }}>
          Location Details
        </div>
        <div style={{ display: "flex", flexWrap: "wrap", gap: 60 }}>
          {/* Delivery Address */}
          <div>
            <div style={{ color: "#6B7280", fontSize: 15, marginBottom: 8, fontWeight: 500 }}>Delivery Address:</div>
            <div style={{ color: "#64748B", fontSize: 16 }}>
             {orderData?.address?.complete_address || "Not available"}

            </div>
          </div>
          {/* Contact Information */}
          <div>
            <div style={{ color: "#6B7280", fontSize: 15, marginBottom: 8, fontWeight: 500 }}>Contact Information:</div>
          <div style={{ color: "#64748B", fontSize: 16 }}>
            {orderData?.username || "Not available"}
            {orderData?.mobile_number ? ` • ${orderData.mobile_number}` : ""}
          </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default TrackOrders;
